import { type FC, type ReactNode, forwardRef } from "react";

import { clsx } from "clsx";

// "wide": hidden under md, where one pane shows at a time. "never" keeps it
// mounted but hidden, so its scroll survives.
export type ReviewPaneShow = "always" | "wide" | "never";

export const reviewPaneShow = (
  hidden: boolean,
  narrowHidden: boolean,
): ReviewPaneShow => {
  if (hidden) return "never";
  return narrowHidden ? "wide" : "always";
};

// A pane's background: slate-50 under the letters, slate-100 under the
// report's pages, "sheet" under the records' pages, white under the
// assistant's notes.
export type PaneBackground = "slate-50" | "slate-100" | "sheet" | "white";

export const PANE_BACKGROUND: Record<PaneBackground, string> = {
  "slate-50": "bg-slate-50",
  "slate-100": "bg-slate-100",
  sheet: "bg-[#f4f6f8]",
  white: "bg-white",
};

// One scrolling half of the records review: the letters or the source pages.
export const ReviewPane = forwardRef<
  HTMLDivElement,
  { show: ReviewPaneShow; background?: PaneBackground; children: ReactNode }
>(({ show, background = "slate-50", children }, ref) => (
  <div
    ref={ref}
    hidden={show === "never"}
    className={clsx(
      "flex-1 rounded-lg border border-slate-200 p-4 [overflow-anchor:none] max-md:w-full md:overflow-y-auto",
      PANE_BACKGROUND[background],
      show === "wide" && "max-md:hidden",
    )}
  >
    {children}
  </div>
));

const STACK_SPACING = {
  tight: "gap-2",
  cards: "gap-5",
  default: "gap-6",
} as const;

// The pane's cards: "tight" while searching, "cards" between letter cards.
export const ReviewPaneStack: FC<{
  spacing?: keyof typeof STACK_SPACING;
  children: ReactNode;
}> = ({ spacing = "default", children }) => (
  <div className={clsx("flex flex-col", STACK_SPACING[spacing])}>
    {children}
  </div>
);

// Stays at the top of a pane while it scrolls, e.g. the assistant's line
// over the letters, the report's pages or the notes.
export const PaneSticky: FC<{
  background: PaneBackground;
  children: ReactNode;
}> = ({ background, children }) => (
  <div
    className={clsx(
      "sticky -top-4 z-10 -mx-4 -mt-4 mb-3 flex flex-col gap-3 px-4 pt-4 pb-1",
      PANE_BACKGROUND[background],
    )}
  >
    {children}
  </div>
);
